import { useEffect, useRef } from 'react';
import { ArrowDown, ArrowUp, Search, X } from 'lucide-react';

interface Props {
  query: string;
  onChange: (v: string) => void;
  /** Total matches (length of indexes computed by parent). */
  total: number;
  /** Active cursor (0-based), or -1 if no match. */
  active: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}

/**
 * Inline search bar. Used by the live logcat tab; fully controlled by the
 * parent which owns the match-index list (so we don't double-compute).
 *
 * Keyboard: Enter = next, Shift-Enter = prev, Esc = close.
 */
export function SearchBar({ query, onChange, total, active, onNext, onPrev, onClose }: Props) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  return (
    <div className="flex items-center gap-2 rounded-md border border-line bg-bg-card px-2 py-1 shadow-sm">
      <Search className="h-3.5 w-3.5 text-ink-muted" />
      <input
        ref={ref}
        className="input min-w-[200px] flex-1 border-0 bg-transparent px-1 py-0.5 text-xs shadow-none focus:ring-0"
        placeholder="查找当前视图…"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) onPrev();
            else onNext();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
      />
      <span className="font-mono text-[10px] tabular-nums text-ink-muted">
        {total > 0 ? `${active + 1} / ${total}` : query ? '无匹配' : '0 / 0'}
      </span>
      <button
        className="rounded p-1 text-ink-muted hover:bg-bg-soft hover:text-ink"
        onClick={onPrev}
        title="上一个 (Shift+Enter)"
        disabled={total === 0}
      >
        <ArrowUp className="h-3.5 w-3.5" />
      </button>
      <button
        className="rounded p-1 text-ink-muted hover:bg-bg-soft hover:text-ink"
        onClick={onNext}
        title="下一个 (Enter)"
        disabled={total === 0}
      >
        <ArrowDown className="h-3.5 w-3.5" />
      </button>
      <button
        className="rounded p-1 text-ink-muted hover:bg-bg-soft hover:text-rose-500"
        onClick={onClose}
        title="关闭 (Esc)"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
